import type { CompetitionDetail, CompetitionType, Student, StudentCaseRow } from "../types/database";
import { isEligibleForCompetition } from "./registrations";
import { supabase } from "./supabase";

/**
 * Registration rows that count as "in" a program for sidebar badge + My Active Cases.
 *
 * Must match the `registration_status` enum exactly: {registered, withdrawn, submitted,
 * disqualified}. There is NO `'active'` value — passing it makes PostgREST reject the
 * whole query with `invalid input value for enum registration_status: "active"`,
 * which silently breaks every consumer (sidebar count, browse exclude list, etc.).
 */
export const MY_ACTIVE_REGISTRATION_STATUSES = ["registered", "submitted"] as const;

/**
 * Columns that actually exist on the `competitions` table — verified against the live schema.
 *
 * Phantom columns previously referenced here (and silently broke every query):
 *   short_description, registration_opens, registration_deadline, program_tags,
 *   prize_pool, results_date — none of those exist. Semantic equivalents we use:
 *     short_description     → brief (truncated at the UI layer)
 *     registration_opens    → opens_at
 *     registration_deadline → closes_at
 *     program_tags          → derived from competition_type
 *     prize_pool            → not modeled yet (always returns null/false)
 *     results_date          → not modeled yet (status enum drives "in progress" check)
 */
export const STUDENT_CASE_SELECT = [
  "id",
  "company_id",
  "name",
  "brief",
  "deliverable_description",
  "status",
  "opens_at",
  "closes_at",
  "team_size_min",
  "max_team_size",
  "competition_type",
  "eligibility_schools",
  "eligibility_majors",
  "eligibility_grad_year_min",
  "eligibility_grad_year_max",
].join(", ");

export type StudentCaseRawRow = {
  id: string;
  company_id: string;
  name: string;
  brief: string | null;
  deliverable_description: string | null;
  status: string;
  opens_at: string | null;
  closes_at: string | null;
  team_size_min: number | null;
  max_team_size: number;
  competition_type: string | null;
  eligibility_schools: string[] | null;
  eligibility_majors: string[] | null;
  eligibility_grad_year_min: number | null;
  eligibility_grad_year_max: number | null;
};

function normalizeCompetitionType(v: unknown): CompetitionType {
  if (v === "uncapped" || v === "invite_only" || v === "capped") return v;
  return "capped";
}

export function studentCaseRowToDetail(row: StudentCaseRow): CompetitionDetail {
  return {
    id: row.id,
    name: row.name,
    brief: row.brief,
    deliverable_description: row.deliverable_description,
    status: row.status,
    closes_at: row.closes_at,
    company_name: row.company_name,
    opens_at: row.opens_at,
    company_id: row.company_id,
    engagement_model: "licensing",
    team_size_min: row.team_size_min,
    max_team_size: row.max_team_size,
    competition_type: row.competition_type,
    max_enrollment: null,
    eligibility_schools: row.eligibility_schools,
    eligibility_majors: row.eligibility_majors,
    eligibility_grad_year_min: row.eligibility_grad_year_min,
    eligibility_grad_year_max: row.eligibility_grad_year_max,
  };
}

export function toStudentCaseRow(row: StudentCaseRawRow, companyName: string): StudentCaseRow {
  const max = row.max_team_size;
  const min = typeof row.team_size_min === "number" && !Number.isNaN(row.team_size_min) ? row.team_size_min : max;
  return {
    id: row.id,
    company_id: row.company_id,
    name: row.name,
    brief: row.brief,
    // short_description doesn't exist as a column; UI uses brief truncated.
    short_description: null,
    deliverable_description: row.deliverable_description,
    status: row.status as StudentCaseRow["status"],
    closes_at: row.closes_at,
    opens_at: row.opens_at,
    company_name: companyName,
    team_size_min: min,
    max_team_size: max,
    competition_type: normalizeCompetitionType(row.competition_type),
    // registration_opens/deadline don't exist; use opens_at/closes_at semantics.
    registration_opens: row.opens_at,
    registration_deadline: row.closes_at,
    eligibility_schools: row.eligibility_schools ?? null,
    eligibility_majors: row.eligibility_majors ?? null,
    eligibility_grad_year_min: row.eligibility_grad_year_min ?? null,
    eligibility_grad_year_max: row.eligibility_grad_year_max ?? null,
    // program_tags/prize_pool/results_date not modeled yet.
    program_tags: null,
    prize_pool: null,
    results_date: null,
  };
}

async function companyNamesFor(ids: string[]): Promise<Map<string, string>> {
  const uniq = [...new Set(ids.filter(Boolean))];
  const out = new Map<string, string>();
  if (uniq.length === 0) return out;
  const { data: companies } = await supabase.from("companies").select("id, name").in("id", uniq);
  for (const c of companies ?? []) {
    if (typeof c.id === "string" && typeof c.name === "string" && c.name.trim()) out.set(c.id, c.name);
  }
  return out;
}

function nowMs(): number {
  return Date.now();
}

function registrationWindowOpen(row: StudentCaseRow): boolean {
  // Without dedicated registration_opens/deadline columns, the registration window
  // is the same as the competition's open window: opens_at → closes_at.
  const now = nowMs();
  if (row.opens_at) {
    const t = new Date(row.opens_at).getTime();
    if (!Number.isNaN(t) && t > now) return false;
  }
  if (row.closes_at) {
    const t = new Date(row.closes_at).getTime();
    if (!Number.isNaN(t) && t <= now) return false;
  }
  return true;
}

function programHasOpened(row: StudentCaseRow): boolean {
  if (!row.opens_at) return true;
  const t = new Date(row.opens_at).getTime();
  if (Number.isNaN(t)) return true;
  return t <= nowMs();
}

function isOpeningSoon(row: StudentCaseRow): boolean {
  if (row.status !== "open") return false;
  if (!row.opens_at) return false;
  const t = new Date(row.opens_at).getTime();
  if (Number.isNaN(t)) return false;
  return t > nowMs();
}

function isActiveBrowse(row: StudentCaseRow): boolean {
  if (row.status !== "open") return false;
  if (!programHasOpened(row)) return false;
  return registrationWindowOpen(row);
}

/** Stable key for category filter — until program_tags lands, use competition_type bucket. */
export function caseCategoryFilterKey(row: StudentCaseRow): string {
  if (row.competition_type === "invite_only") return "Invite only";
  if (row.competition_type === "uncapped") return "Open enrollment";
  return "Case competition";
}

export function caseCategoryOverline(row: StudentCaseRow): string {
  if (row.competition_type === "invite_only") return "INVITE ONLY · CASE COMPETITION";
  if (row.competition_type === "uncapped") return "OPEN ENROLLMENT · CASE COMPETITION";
  return "CASE COMPETITION";
}

/** Human format label for filters + meta (Strategy / team sizing pattern). */
export function caseFormatLabel(row: StudentCaseRow): string {
  const typeLabel =
    row.competition_type === "invite_only"
      ? "Invite only"
      : row.competition_type === "uncapped"
        ? "Uncapped"
        : "Case competition";
  const { team_size_min: a, max_team_size: b } = row;
  const team = a === b ? `Team (${b})` : `Teams ${a}–${b}`;
  return `${typeLabel} · ${team}`;
}

export function caseTeamSizePhrase(row: StudentCaseRow): string {
  const { team_size_min: a, max_team_size: b } = row;
  return a === b ? `Teams of ${b}` : `Teams of ${a}–${b}`;
}

export function daysRemainingUntilClose(closesAt: string | null): number | null {
  if (!closesAt) return null;
  const end = new Date(closesAt).getTime();
  if (Number.isNaN(end)) return null;
  const d = Math.ceil((end - nowMs()) / 86_400_000);
  return d;
}

/** Stub until a real prize_pool column exists. Always false today. */
export function prizePoolMentioned(_row: StudentCaseRow): boolean {
  return false;
}

/** Competition still in progress for a registered student. */
export function competitionStillInProgressForMyCase(row: StudentCaseRow): boolean {
  if (row.status === "closed" || row.status === "draft") return false;
  return row.status === "open" || row.status === "judging";
}

/**
 * Open programs the student may be eligible for: active (registration open) vs opening soon (opens_at in future).
 * Reuses the same school/major/grad-year rules as registration (`isEligibleForCompetition`).
 * @param excludeCompetitionIds Competitions to omit (e.g. already enrolled — browse list only).
 */
export async function listOpenCasesForStudent(
  student: Student | null,
  excludeCompetitionIds?: string[]
): Promise<{
  active: StudentCaseRow[];
  soon: StudentCaseRow[];
}> {
  const exclude = new Set((excludeCompetitionIds ?? []).filter(Boolean));

  const { data: rows, error } = await supabase
    .from("competitions")
    .select(STUDENT_CASE_SELECT)
    .eq("status", "open")
    .order("closes_at", { ascending: true, nullsFirst: false });

  if (error || !Array.isArray(rows) || rows.length === 0) return { active: [], soon: [] };

  const raw = rows as unknown as StudentCaseRawRow[];
  const nameByCompany = await companyNamesFor(raw.map((r) => r.company_id));
  const mapped: StudentCaseRow[] = [];
  for (const r of raw) {
    const cn = nameByCompany.get(r.company_id);
    if (!cn) continue;
    const row = toStudentCaseRow(r, cn);
    if (exclude.has(row.id)) continue;
    if (!isEligibleForCompetition(student, studentCaseRowToDetail(row))) continue;
    mapped.push(row);
  }

  const active: StudentCaseRow[] = [];
  const soon: StudentCaseRow[] = [];
  for (const row of mapped) {
    if (isOpeningSoon(row)) soon.push(row);
    else if (isActiveBrowse(row)) active.push(row);
  }

  const closesMs = (s: string | null) => (s ? new Date(s).getTime() : Number.POSITIVE_INFINITY);
  active.sort((a, b) => closesMs(a.closes_at) - closesMs(b.closes_at));
  soon.sort((a, b) => {
    const ta = a.opens_at ? new Date(a.opens_at).getTime() : 0;
    const tb = b.opens_at ? new Date(b.opens_at).getTime() : 0;
    return ta - tb;
  });

  return { active, soon };
}


/**
 * Tier classification for the v2 Find Competition flow.
 *
 * Until a dedicated `competition_tier` column exists on the competitions
 * table, we infer from the eligibility-schools list — the same field
 * that already gates registration:
 *   - 0 schools (or null) → National (open to anyone eligible by major /
 *     grad year)
 *   - 1 school           → Campus (single-school program)
 *   - 2+ schools         → Regional (multi-school slate)
 *
 * This is a heuristic, not a contract. When the schema gets a real tier
 * field, swap this implementation to read it directly without changing
 * the call sites.
 */
export type CompetitionTier = "national" | "campus" | "regional";

export function competitionTier(row: { eligibility_schools: string[] | null }): CompetitionTier {
  const schools = row.eligibility_schools ?? [];
  if (schools.length === 0) return "national";
  if (schools.length === 1) return "campus";
  return "regional";
}

export function competitionTierLabel(tier: CompetitionTier): string {
  if (tier === "national") return "National";
  if (tier === "campus") return "Campus";
  return "Regional";
}
